import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { IconPin, IconCalendar } from './Icons';
import './GalleryLightbox.css';

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.95
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    scale: 0.95
  })
};

export default function GalleryLightbox({ images, currentIndex, onClose, onPrev, onNext }) {
  const image = images[currentIndex];
  const [direction, setDirection] = useState(1);
  const prevIndexRef = useRef(currentIndex);
  
  // Calculate navigation direction (only when currentIndex changes)
  if (currentIndex !== prevIndexRef.current) {
    setDirection(currentIndex > prevIndexRef.current ? 1 : -1);
    prevIndexRef.current = currentIndex;
  }

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape')     onClose();
    if (e.key === 'ArrowLeft')  onPrev();
    if (e.key === 'ArrowRight') onNext();
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  if (!image) return null;

  return createPortal(
    <div className="lightbox" role="dialog" aria-modal="true" aria-label="Photo viewer">
      {/* Backdrop */}
      <div className="lightbox__backdrop" onClick={onClose} />

      {/* Modal */}
      <div className="lightbox__modal">
        {/* Image pane */}
        <div className="lightbox__img-pane">
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <motion.img
              key={currentIndex}
              src={image.src}
              alt={image.caption}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 280, damping: 28 },
                opacity: { duration: 0.2 },
                scale: { duration: 0.2 }
              }}
              className="lightbox__img"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </AnimatePresence>
          
          {/* Nav */}
          <button
            className="lightbox__nav lightbox__nav--prev"
            onClick={onPrev}
            disabled={currentIndex === 0}
            aria-label="Previous photo"
          >‹</button>
          <button
            className="lightbox__nav lightbox__nav--next"
            onClick={onNext}
            disabled={currentIndex === images.length - 1}
            aria-label="Next photo"
          >›</button>
        </div>

        {/* Info pane */}
        <div className="lightbox__info">
          <div className="lightbox__meta-top">
            <span className="badge badge-navy">{image.category}</span>
            <span className="lightbox__counter">{currentIndex + 1} / {images.length}</span>
          </div>
          <h3 className="lightbox__caption">{image.caption}</h3>
          {image.location && (
            <p className="lightbox__loc" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><IconPin size={14} /> {image.location}</p>
          )}
          {image.date && (
            <p className="lightbox__date" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><IconCalendar size={14} /> {image.date}</p>
          )}
          {image.tags?.length > 0 && (
            <div className="lightbox__tags">
              {image.tags.map(t => <span key={t} className="badge badge-navy">{t}</span>)}
            </div>
          )}
        </div>

        {/* Close */}
        <button className="lightbox__close" onClick={onClose} aria-label="Close">✕</button>
      </div>
    </div>,
    document.body
  );
}
